import { LayoutDashboard, Calendar, HeartPulse, ClipboardCheck, Package, User, LogOut } from "lucide-react";
import companyLogo from "@/assets/ulture-logo-transparent.png";
import { useTranslation } from "react-i18next";
import { NavLink } from "@/components/NavLink";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { NotificationDropdown } from "@/components/notifications";
import { PeritoCompanySwitcher } from "@/components/perito/PeritoCompanySwitcher";

/**
 * Barra lateral del portal del PERITO.
 *
 * Es la misma barra que `AppSidebar` (admin/manager) pieza por pieza: mismo
 * degradado (`app-sidebar-gradient`), mismo plegado a iconos, mismo chip blanco
 * del logo, mismas alturas, tipografías y tamaños en cada breakpoint, mismos
 * grupos con su rótulo en versalitas y el mismo pie fijado abajo tras una línea
 * fina. Lo único que cambia son las páginas: el perito no tiene Mapa, Campañas,
 * Registros, Tienda, Equipo, Analíticas ni Ajustes.
 *
 * No se reutiliza `AppSidebar` directamente porque arrastra el onboarding
 * (`useOnboarding` revienta fuera de su provider) y los permisos de manager, que
 * en este portal no pintan nada. Deliberadamente NO se ha tocado `AppSidebar`:
 * el encargo era no mover nada de admin/manager.
 */

interface MenuItem {
  title: string;
  url: string;
  icon: React.ComponentType<{ className?: string }>;
  tooltip?: string;
}

interface MenuGroup {
  /** Rótulo de sección; el primer grupo va sin él, como en la de admin. */
  label?: string;
  items: MenuItem[];
}

export function PeritoSidebar() {
  const { t } = useTranslation();
  const { state, isMobile, setOpenMobile } = useSidebar();
  const { company, signOut, linkedCompanies, profile, switchCompany } = useAuth();
  /* Con más de una empresa, el nombre fijo pasa a ser el conmutador (tanda 5,
     A9 / T01-04): en el ordenador la única salida era cerrar sesión. */
  const variasEmpresas = linkedCompanies.length > 1;
  const isCollapsed = state === "collapsed";

  const handleNavClick = () => {
    if (!isMobile) return;
    setOpenMobile(false);
  };

  const menuGroups: MenuGroup[] = [
    {
      items: [{ title: t("perito.nav.inicio"), url: "/perito/inicio", icon: LayoutDashboard }],
    },
    {
      label: t("nav.groups.operations", "Operación"),
      items: [
        { title: t("perito.nav.sanidad"), url: "/perito/sanidad", icon: HeartPulse },
        { title: t("perito.nav.planificador"), url: "/perito/planificador", icon: Calendar },
        { title: t("perito.nav.seguimiento"), url: "/perito/seguimiento", icon: ClipboardCheck },
      ],
    },
    {
      label: t("nav.groups.resources", "Recursos"),
      items: [{ title: t("perito.nav.inventario"), url: "/perito/inventario", icon: Package }],
    },
  ];

  // El perito no tiene Ajustes: su equivalente en el pie es «Mi perfil».
  const profileItem: MenuItem = { title: t("perito.nav.perfil"), url: "/perito/perfil", icon: User };

  // Mismas clases exactas que `AppSidebar.renderItem`, para que las filas midan
  // y pesen igual en los dos portales.
  const rowClass = cn(
    "hover:!text-white",
    isCollapsed
      ? "!h-[44px] !w-[44px] !p-0 [&>svg]:!w-5 [&>svg]:!h-5 2xl:!h-[52px] 2xl:!w-[52px] 2xl:[&>svg]:!w-6 2xl:[&>svg]:!h-6"
      : "!h-[34px] [&>svg]:!w-[18px] [&>svg]:!h-[18px] 2xl:!h-[40px] 2xl:[&>svg]:!w-[22px] 2xl:[&>svg]:!h-[22px]",
  );
  const linkClass = cn(
    "flex items-center rounded-[11px] transition-all duration-150 text-white font-['Poppins'] hover:bg-white/[0.12] hover:!text-white",
    isCollapsed
      ? "justify-center font-bold"
      : "gap-[11px] px-[13px] text-[13px] font-bold md:text-[13.5px] md:font-semibold 2xl:text-[15px] 2xl:gap-[13px] 2xl:px-[15px]",
  );

  const renderItem = (item: MenuItem) => (
    <SidebarMenuItem key={item.url} className="animate-in fade-in slide-in-from-left-2 duration-300">
      <SidebarMenuButton asChild tooltip={item.tooltip ?? item.title} className={rowClass}>
        <NavLink to={item.url} className={linkClass} activeClassName="ipm-pill-active !text-white" onClick={handleNavClick}>
          <item.icon />
          {!isCollapsed && <span className="flex-1 truncate">{item.title}</span>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  return (
    <Sidebar variant="floating" collapsible="icon" className="app-sidebar-gradient border-none md:z-30">
      <SidebarHeader className={cn("md:border-b md:border-white/15 relative z-10", isCollapsed ? "p-2" : "p-4")}>
        <div className={cn("flex items-center gap-3 relative", isCollapsed && "justify-center")}>
          <div className="flex items-center justify-center flex-shrink-0 w-9 h-9 2xl:w-11 2xl:h-11 rounded-[11px] bg-white shadow-[0_3px_10px_rgba(8,30,45,0.18)]">
            <img src={companyLogo} alt="Ulture Pest" className="object-contain w-[30px] h-[30px] 2xl:w-[36px] 2xl:h-[36px]" />
          </div>
          {!isCollapsed && (
            <div className="flex-1 min-w-0 pr-12">
              <h2 className="font-['Poppins'] font-bold text-base 2xl:text-lg text-white leading-tight">
                {t("auth.platformTitle")}
              </h2>
              {variasEmpresas ? (
                <PeritoCompanySwitcher
                  companies={linkedCompanies}
                  currentCompanyId={profile?.company_id || null}
                  onSwitch={switchCompany}
                  variant="inline"
                />
              ) : company && (
                <p className="font-['Mulish'] font-bold text-[11.5px] 2xl:text-[13px] text-white/[0.82] truncate">
                  {company.name}
                </p>
              )}
            </div>
          )}
          {isCollapsed && variasEmpresas && (
            <PeritoCompanySwitcher
              companies={linkedCompanies}
              currentCompanyId={profile?.company_id || null}
              onSwitch={switchCompany}
              variant="icon"
            />
          )}
          {!isCollapsed && (
            <div className="absolute top-0 right-0 z-20 [&_svg]:text-white [&_button]:bg-white/10 [&_button:hover]:bg-white/25 [&_button]:rounded-xl">
              <NotificationDropdown collapsed={isCollapsed} />
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent className="opacity-100 relative z-10 flex flex-col overflow-hidden">
        <div className="flex-1 min-h-0 overflow-y-auto">
          {menuGroups.map((group, gi) => (
            <SidebarGroup
              key={group.label ?? `general-${gi}`}
              className={cn("px-2", gi === 0 ? "pt-1 pb-0.5" : "pt-0.5 pb-0.5")}
            >
              {group.label && !isCollapsed && (
                <SidebarGroupLabel className="h-[18px] 2xl:h-[22px] px-[13px] mb-px font-['Poppins'] font-semibold uppercase tracking-[0.1em] text-[9.5px] 2xl:text-[11px] text-white/50">
                  {group.label}
                </SidebarGroupLabel>
              )}
              <SidebarGroupContent>
                <SidebarMenu className={cn("space-y-0.5", isCollapsed && "space-y-2 items-center")}>
                  {group.items.map(renderItem)}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        </div>

        {/* Pie fijado abajo tras la línea fina, igual que en la de admin. */}
        <SidebarGroup className="mt-auto pb-3 pt-2 border-t border-white/[0.16]">
          <SidebarGroupContent>
            <SidebarMenu className={cn("space-y-0.5", isCollapsed && "space-y-2.5 items-center")}>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={profileItem.title} className={rowClass}>
                  <NavLink
                    to={profileItem.url}
                    className={linkClass}
                    activeClassName="ipm-pill-active !text-white"
                    onClick={handleNavClick}
                  >
                    <profileItem.icon />
                    {!isCollapsed && <span>{profileItem.title}</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t("auth.signOut")} className={rowClass}>
                  <button
                    onClick={signOut}
                    className={cn(
                      "flex items-center rounded-[11px] transition-all duration-150 font-['Poppins'] text-white/90 hover:bg-white/[0.12] hover:!text-white w-full text-left",
                      isCollapsed
                        ? "justify-center font-bold"
                        : "gap-[11px] px-[13px] text-[13px] font-bold md:text-[13.5px] md:font-semibold 2xl:text-[15px] 2xl:gap-[13px] 2xl:px-[15px]",
                    )}
                  >
                    <LogOut />
                    {!isCollapsed && <span>{t("auth.signOut")}</span>}
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}
